import { Spinner } from "@/components/ui/spinner";
import MessageChatInterface from "./MessageChatInterface";
import ActivityMessage from "./ActivityMessage";

// Type definitions
export type MessageItem = {
  type: "message";
  timestamp: string;
  message: {
    id?: string;
    conversationId: string;
    content: string;
    source: string;
    sourceId?: string;
    createdAt: string;
    lastStatus?: string;
    audio?: { url: string; transcription?: string };
    image?: { url: string };
    video?: { url: string };
    document?: { url: string };
    toPhoneNumber?: string;
    isPrimaryConnection?: boolean;
    isOptimistic?: boolean;
    resendable?: boolean;
    /** Raw Meta error code on a failed send, and the catalog's Spanish explanation
     *  of it (both computed server-side in merchantPortal/conversations.ts). */
    failureCode?: number;
    failureReason?: string;
  };
};

export type ActivityItem = {
  type: "activity";
  timestamp: string;
  activity: {
    id: string;
    merchantId: string;
    leadId: string;
    type:
      | "conversationStarted"
      | "assignment"
      | "pipelinePositionChanged"
      | "appointment"
      | "takenByEmployee"
      | "agentTurnedOff"
      | "agentTurnedOn"
      | "aiAgentAssignment"
      | "manualReassignment"
      | "partnerCrmReassignment";
    conversationStarted?: { startedAt: string };
    assignment?: {
      employeeName: string;
      assignedAt: string;
      /** Frozen Spanish explanation, rendered at decision time. */
      rationaleText?: string;
      /** JSON-serialised AssignmentTrace. */
      rationaleTrace?: string;
    };
    pipelinePositionChanged?: {
      pipelinePosition: string;
      trigger: string;
      changedAt: string;
      /** Portal user who moved the lead. Absent on automated moves and pre-change activities. */
      changedBy?: string;
    };
    appointment?: { date: string; time: string; employeeName: string };
    takenByEmployee?: { employeeName: string; takenAt: string };
    agentTurnedOff?: { reason: string; turnedAt: string; turnedBy: string };
    agentTurnedOn?: { reason: string; turnedAt: string; turnedBy: string };
    aiAgentAssignment?: { topic: string; pipelineId?: string; changedAt: string };
    manualReassignment?: {
      newEmployeeName: string;
      previousEmployeeName?: string;
      reassignedAt: string;
      /** Portal user who performed the handover. Absent on pre-change activities. */
      reassignedBy?: string;
    };
    partnerCrmReassignment?: {
      employeeName: string;
      previousEmployeeName?: string;
      assignedAt: string;
    };
    createdAt: string;
    updatedAt: string;
  };
};

export type NoteItem = {
  type: "note";
  timestamp: string;
  note: {
    id: string;
    body: string;
    authorEmail: string;
    authorName?: string;
    createdAt: string;
    editedAt?: string;
  };
};

export type TimelineItem = MessageItem | ActivityItem | NoteItem;

interface ConversationTimelineProps {
  timelineData: TimelineItem[];
  isLoading: boolean;
  allPositions: Array<{ id: string; displayName: string; stage: string }>;
  onRetryMessage?: (messageId: string) => void;
}

const ConversationTimeline = ({
  timelineData,
  isLoading,
  allPositions,
  onRetryMessage,
}: ConversationTimelineProps) => {
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <Spinner size="lg" />
          <p className="text-center text-gray-500">Cargando conversación...</p>
        </div>
      </div>
    );
  }

  if (timelineData.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <p className="text-center text-gray-500">No hay mensajes en esta conversación</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col py-4">
      {timelineData.map((item) => {
        switch (item.type) {
          case "message":
            return (
              <MessageChatInterface
                key={`msg-${item.message.id || item.message.createdAt}`}
                message={item.message}
                onRetry={
                  item.message.id &&
                  onRetryMessage &&
                  !item.message.isOptimistic &&
                  item.message.resendable
                    ? () => onRetryMessage(item.message.id!)
                    : undefined
                }
              />
            );
          case "activity":
            return (
              <ActivityMessage
                key={`activity-${item.activity.id}`}
                activity={item.activity}
                allPositions={allPositions}
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

export default ConversationTimeline;
